
import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslation } from "react-i18next";
import { useMobileMenu } from "@/contexts/MobileMenuContext";
import LanguageSwitcher from "./LanguageSwitcher";
import { cn } from "@/lib/utils";
import NavbarBrand from "./navbar/NavbarBrand";
import NavbarMenu from "./navbar/NavbarMenu";
import NavbarActions from "./navbar/NavbarActions";
import MobileNavbar from "./navbar/MobileNavbar";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const { isMenuOpen, toggleMenu, closeMenu } = useMobileMenu();
  const isHomePage = location.pathname === '/';

  // Enhanced scroll handling with throttling for better performance
  useEffect(() => {
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const offset = window.scrollY;
          setScrolled(offset > 20);
          ticking = false;
        });
        ticking = true;
      }
    };
    
    window.addEventListener("scroll", handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <>
      <header 
        className={cn(
          "fixed w-full z-50 transition-all duration-500 ease-out top-0 left-0 right-0",
          scrolled 
            ? "bg-propmatch-dark/98 backdrop-blur-2xl shadow-2xl shadow-black/20" 
            : "bg-transparent",
          "will-change-[background-color,box-shadow,border]"
        )}
        role="banner"
      >
        <div className="container mx-auto px-2 sm:px-3 md:px-4 lg:px-6 py-1.5 sm:py-2 md:py-2.5 lg:py-3">
          <div className="flex items-center justify-between w-full">
            <NavbarBrand />

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-4 xl:space-x-6">
              <NavbarMenu />
              <div className="flex items-center space-x-2 xl:space-x-3">
                <NavbarActions />
                <LanguageSwitcher 
                  variant="glass" 
                  size="sm"
                  className="opacity-90 hover:opacity-100 transition-all duration-200" 
                />
              </div>
            </div>

            {/* Tablet Navigation */}
            <div className="hidden md:flex lg:hidden items-center space-x-2">
              <NavbarMenu />
              <div className="flex items-center space-x-1.5">
                <NavbarActions />
                <LanguageSwitcher 
                  size="sm"
                  variant="glass"
                  className="opacity-90 hover:opacity-100 transition-all duration-200 scale-90" 
                />
              </div>
            </div>

            {/* Mobile Navigation */}
            <div className="md:hidden flex items-center space-x-1.5">
              <LanguageSwitcher 
                size="sm"
                variant="glass"
                showAnimation={false}
                className="px-1.5 py-1 text-xs opacity-90 hover:opacity-100"
              />
              
              <button 
                onClick={toggleMenu} 
                className={cn(
                  "relative p-2 sm:p-2.5 rounded-xl transition-all duration-300 ease-out touch-manipulation",
                  "bg-white/8 hover:bg-white/12 active:bg-white/20 backdrop-blur-2xl",
                  "border border-white/15 hover:border-white/25",
                  "shadow-lg hover:shadow-xl hover:shadow-propmatch-blue/10",
                  isMenuOpen ? "rotate-180 scale-110" : "rotate-0 scale-100",
                  "group"
                )}
                aria-label={isMenuOpen ? t("nav.closeMenu") : t("nav.openMenu")}
                aria-expanded={isMenuOpen}
                aria-controls="mobile-menu"
                role="button"
              >
                {/* Enhanced animated hamburger/close icon */}
                <div className="relative w-5 h-5 flex items-center justify-center">
                  <Menu 
                    size={18} 
                    className={cn(
                      "absolute transition-all duration-300 ease-out text-white",
                      isMenuOpen ? "opacity-0 rotate-180 scale-75" : "opacity-100 rotate-0 scale-100"
                    )} 
                  />
                  <X 
                    size={18} 
                    className={cn(
                      "absolute transition-all duration-300 ease-out text-white",
                      isMenuOpen ? "opacity-100 rotate-0 scale-100" : "opacity-0 rotate-180 scale-75"
                    )} 
                  />
                </div>
                
                {/* Enhanced ripple effect */}
                <div className="absolute inset-0 rounded-xl overflow-hidden">
                  <div className="absolute inset-0 bg-white/10 scale-0 rounded-xl transition-transform duration-300 group-active:scale-150" />
                </div>
                
                {/* Enhanced glow effect when active */}
                {isMenuOpen && (
                  <div className="absolute inset-0 rounded-xl bg-propmatch-blue/30 blur-md animate-pulse" />
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Enhanced Mobile menu */}
      <MobileNavbar isOpen={isMenuOpen} onClose={closeMenu} />
    </>
  );
};

export default Navbar;
